/* eslint-disable indent */
import { useResponsiveFontSize } from '@/hooks/useResponsiveDimensions';
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';
import { StyleSheet, View } from 'react-native';
import { OfflineWrapper } from '@/contexts/OfflineWrapper';
import { useResultBuffer } from '@/hooks/useResultBuffer';
import { isPatientApp } from '@/config/constants';
import { useAssetDownloader } from '@/contexts/DownloadAssetContext';
import { ImageExercise } from '@/components/ImageExercise';
import { VideoPlayer } from '@/components/Video';
import { SpeakingFeedback } from './SpeakingFeedback';
import { SpeakingHeader } from './SpeakingHeader';

const gap = 50;

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: gap / -2,
    marginTop: 15,
  },
  headerItem: {
    marginHorizontal: gap,
  },
  imageSpeakerContainer: {
    flex: 1,
    flexDirection: 'row',
    justifyContent: 'space-evenly',
    alignItems: 'center',
  },
});

const generateExerciseType = (setting, name) => {
  return {
    ...setting,
    name,
  };
};

const generateSetItemType = (key) => `${key}setitem`;

function SpeakingExercise(
  {
    exercise,
    currentExercise,
    settings,
    selectedPerformance,
    patientId,
    changeSelectedPerformance,
    isSelf,
    next,
    isRecordingDisabled,
    isRecording,
    setIsRecording,
  },
  ref,
) {
  const { getLocalUri } = useAssetDownloader();

  const { setting, value, playbackRate } = settings;
  const correctSelected = useMemo(
    () => (selectedPerformance === 'none' ? false : true),
    [selectedPerformance],
  );

  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const [isAudioPlaying, setIsAudioPlaying] = useState(false);
  const timeoutSeconds = value + '000';
  const isNoRecord = setting == 'norecord';
  const setItemType = generateSetItemType(exercise.exerciseClass);
  const videoUrl = exercise?.video ?? '';
  const audioUrl = exercise?.audio ?? '';
  const isSentence = exercise?.exerciseClass == 'sentence';

  const { resultCreator } = useResultBuffer({
    setItemType,
    setItemTypeId: exercise.wordsetitem,
    exerciseType: generateExerciseType(settings, exercise?.type),
    patientId,
    shouldTracksMistakes: false,
    selectedPerformance,
    isSelf,
  });

  const handleIsVideoPlaying = useCallback((value) => {
    setIsVideoPlaying(value);
  }, []);
  const handleIsRecording = useCallback((value) => {
    setIsRecording(value);
  }, []);
  const handleIsAudioPlaying = useCallback((value) => {
    setIsAudioPlaying(value);
  }, []);

  const [correctImage, setCorrectImage] = useState('');

  const responsiveStyles = StyleSheet.create({
    imageContainer: {
      width: useResponsiveFontSize(isPatientApp ? 28 : 30),
      aspectRatio: 1,
    },
    speakerContainer: {
      width: useResponsiveFontSize(16),
      aspectRatio: 1,
    },
  });

  const generateCorrectImage = useCallback(async (exercise) => {
    if (exercise.retinaImage) {
      const localCorrectImage = await getLocalUri(exercise.retinaImage);
      setCorrectImage(localCorrectImage);
    } else {
      const localCorrectImage = await getLocalUri(exercise.correct[0]);
      setCorrectImage(localCorrectImage);
    }
  }, []);

  const images = useMemo(() => {
    if (!correctImage) {
      return [];
    }
    return isSelf
      ? [
          <View style={responsiveStyles.imageContainer} key={correctImage}>
            <OfflineWrapper key={correctImage} url={correctImage}>
              <ImageExercise isCorrect notClickable />
            </OfflineWrapper>
          </View>,
          <View style={responsiveStyles.speakerContainer} key="Player">
            <OfflineWrapper url={isSentence ? audioUrl : videoUrl}>
              <VideoPlayer
                noVideo={isSentence}
                noStyles
                handleIsVideoPlaying={handleIsVideoPlaying}
                isRecording={isRecording}
                isAudioPlaying={isAudioPlaying}
                correctSelected={correctSelected}
                onCorrect={next}
                playbackRate={playbackRate}
              />
            </OfflineWrapper>
          </View>,
        ]
      : [
          <View style={responsiveStyles.imageContainer} key={correctImage}>
            <OfflineWrapper url={correctImage}>
              <ImageExercise isCorrect notClickable />
            </OfflineWrapper>
          </View>,
        ];
  }, [
    correctImage,
    isSentence,
    audioUrl,
    videoUrl,
    isRecording,
    isAudioPlaying,
    correctSelected,
  ]);
  const onThumbsUp = () => {
    const value = 'correct';
    resultCreator(null, 'Richtig');
    changeSelectedPerformance(value)();
  };
  const onThumbs = () => {
    const value = 'almost';
    resultCreator(null, 'Fast');
    changeSelectedPerformance(value)();
  };
  const onThumbsDown = () => {
    const value = 'wrong';
    resultCreator(null, 'Falsch');
    changeSelectedPerformance(value)();
  };

  useImperativeHandle(ref, () => ({
    resultCreator,
  }));

  useEffect(() => {
    generateCorrectImage(exercise);
  }, [currentExercise]);

  return (
    <View style={styles.container}>
      {!isNoRecord ? (
        isSelf ? (
          <View style={styles.header}>
            <View style={styles.headerItem}>
              <SpeakingHeader
                timeoutSeconds={timeoutSeconds}
                currentExercise={currentExercise}
                isVideoPlaying={isVideoPlaying}
                handleIsRecording={handleIsRecording}
                handleIsAudioPlaying={handleIsAudioPlaying}
                selectedPerformance={selectedPerformance}
                abortPlayingRecording={correctSelected}
              />
            </View>
            <View style={styles.headerItem}>
              <SpeakingFeedback
                isRecording={isRecording}
                onThumbsUp={onThumbsUp}
                onThumbs={onThumbs}
                onThumbsDown={onThumbsDown}
                selectedPerformance={selectedPerformance}
              />
            </View>
          </View>
        ) : (
          <SpeakingHeader
            timeoutSeconds={timeoutSeconds}
            currentExercise={currentExercise}
            isVideoPlaying={isVideoPlaying}
            handleIsRecording={handleIsRecording}
            handleIsAudioPlaying={handleIsAudioPlaying}
            selectedPerformance={selectedPerformance}
            abortPlayingRecording={correctSelected}
            isRecordingDisabled={isRecordingDisabled}
          />
        )
      ) : isSelf ? (
        <View style={styles.header}>
          <View style={styles.headerItem} />
          <View style={styles.headerItem}>
            <SpeakingFeedback
              isRecording={isRecording}
              onThumbsUp={onThumbsUp}
              onThumbs={onThumbs}
              onThumbsDown={onThumbsDown}
              selectedPerformance={selectedPerformance}
            />
          </View>
        </View>
      ) : (
        <View style={styles.header} />
      )}
      <View style={styles.imageSpeakerContainer}>
        {images.map((item) => {
          return item;
        })}
      </View>
    </View>
  );
}

export default forwardRef(SpeakingExercise);
